import { HeadingItem, CatchWeightCard } from 'components';
import { TripContext } from 'contexts';
import React, { FC, Fragment, useContext, useState } from 'react';
import { ReasonClass } from 'services/enums';
import { CatchWeight, LineItem, Order, OrderItem, ReconcileReason } from 'services/types';
import './po-card-lists.scss';
import { CatchWeightOptions } from 'enums'

type Props = {
    items?: CatchWeight[],
    onClickOperationMode?: (options: CatchWeightOptions, id: number, editedWeight: number) => void,
    onAddCatchWeight?: (orderId?: number) => void,
    onClickShowVerifiedButton?: () => void,
    isShowVerifiedItemsEnabled?: boolean,
    stopId: number | undefined
    lineItemId: number
}

type CatchWeightItem = {
    deliveredQuantity: number | undefined,
    deliveredWeight: number | undefined,
    countedQuantity: number | undefined,
    countedWeight: number | undefined
}

export const POCardLists: FC<Props> = ({ stopId, lineItemId, onClickOperationMode = () => { }, onAddCatchWeight = () => { }}) => {
    const tripContext = useContext(TripContext)
    const orders: Order[] = tripContext.orders.filter(o => o.stopId == stopId)
    const lineItem: LineItem = tripContext.lineItems.find(li => li.id == lineItemId) ?? {} as LineItem
    const reconcileReasons: ReconcileReason[] = tripContext.reconcileReasons.toArray() ?? []
    const [verifiedItemIds, setVerifiedItemIds] = useState<number[]>([])

    let items: OrderItem[] = []
    let catchWeightItems: CatchWeight[] = []

    orders.forEach(order => {
        items = [...items, ...tripContext.orderItems.filter(oi => oi.orderId == order.id && oi.lineItemId == lineItemId)]
    })

    items.forEach(orderItem => {
        catchWeightItems = [...catchWeightItems, ...tripContext.catchWeights.filter(cw => cw.orderItemId == orderItem.id)]
    })

    const getCountedItems = (id: number): CatchWeight[] => catchWeightItems.filter(x => x.isVerified && x.orderItemId === id && (x.reconcileReasonId == null || reconcileReasons.some(reason => reason.id == x.reconcileReasonId && reason.class != ReasonClass.Shortage))) ?? []

    const getDeliveredItems = (id: number): CatchWeight[] => getCountedItems(id).filter(x => x.reconcileReasonId == null || reconcileReasons.some(reason => reason.id == x.reconcileReasonId && reason.isRefusal == false))

    const calculateCountedQuantity = (id: number): number => getCountedItems(id).length

    const calculateCountedWeight = (id: number): number => getCountedItems(id).reduce((acc, x) => (acc + (x.adjustedWeight ? x.adjustedWeight : x.shippedWeight)), 0)

    const calculateDeliveredQuantity = (id: number): number => getDeliveredItems(id).length

    const calculateDeliveredWeight = (id: number): number => getDeliveredItems(id).reduce((acc, x) => acc + (x.adjustedWeight ? x.adjustedWeight : x.shippedWeight), 0)

    const getItemInfo = (id: number) => {
        let itemInfo: CatchWeightItem = {
            countedQuantity: calculateCountedQuantity(id),
            countedWeight: +calculateCountedWeight(id).toFixed(3),
            deliveredQuantity: calculateDeliveredQuantity(id),
            deliveredWeight: +calculateDeliveredWeight(id).toFixed(3)
        }
        return itemInfo
    }

    const onVerificationEnabled = (id?: number) => {
        const itemIds = verifiedItemIds.includes(id ?? 0) ? verifiedItemIds.filter((x: number) => x != id) : [...verifiedItemIds, id]
        setVerifiedItemIds(itemIds as [])
    }

    return (
        <Fragment>
            {items.map((item, index) => (
                <div className={'card-material'} key={index}>
                    <HeadingItem text={`PO: ${item.customerPoNumber ?? 'Unknown'}`} />
                    <div className={'item-qty-card-details'}>
                        <div>Ordered: {item.orderedQuantity}</div>
                        <div>Shipped: {item.shippedQuantity}</div>
                        <div>Lbs: {item.shippedWeight}</div>
                        <div>UOM: {lineItem && lineItem.unitOfMeasure}</div>
                    </div>
                    <div className={'item-qty-card-sub-heading'}>
                        <div style={{ width: '34%' }}></div>
                        <div className={'item-qty-card-sub-heading-text'}>Quantity</div>
                        <div className={'item-qty-card-sub-heading-text'}>Lbs</div>
                    </div>
                    <div className={'item-qty-card-row'}>
                        <div className={'item-qty-card-adjusted-text'} style={{ width: '34%' }}>Counted</div>
                        <div className={'item-qty-card-adjusted-text'}>{getItemInfo(item.id).countedQuantity}</div>
                        <div className={'item-qty-card-adjusted-text'}>{getItemInfo(item.id).countedWeight}</div>
                    </div>
                    <div className={'item-qty-card-row'}>
                        <div className={'item-qty-card-adjusted-text'} style={{ width: '34%' }}>Delivered</div>
                        <div className={'item-qty-card-adjusted-text'}>{getItemInfo(item.id).deliveredQuantity}</div>
                        <div className={'item-qty-card-adjusted-text'}>{getItemInfo(item.id).deliveredWeight}</div>
                    </div>
                    <CatchWeightCard
                        stopId={stopId}
                        lineItemId={lineItemId}
                        onClickOperationMode={onClickOperationMode}
                        onAddCatchWeight={onAddCatchWeight}
                        onClickShowVerifiedButton={onVerificationEnabled}
                        isShowVerifiedItemsEnabled={!verifiedItemIds.includes(item.id)}
                        orderId={item?.id}
                    />
                </div>
            ))}
        </Fragment>
    )
}